import {naturalSortByFunction} from '@benchling/js-utils';
import _ from 'underscore';

import {ResolvedChipItemIdMap} from 'insights/analytics-visualizations/chip-items/ResolvedChipItemTypes';
import {resolveCell} from 'insights/analytics-visualizations/chip-items/useResolvedRunQueryOutput';

import {Row, RunQueryOutputLegacyData} from './AnalyticsWarehouseTypes';
import cellToString from './cellToString';
import {RunQueryOutputTableSortState} from './useRunQueryOutputTableSortState';

/**
 * Determines whether or not a column in the RunQueryOutput is numeric.
 * Assumes all columns have uniform & nullable types.
 *
 * Short circuits upon finding first non-null value. Returns false if all values are null.
 *
 * Examples:
 * columnIsNumeric({..., rows: [[null],[3]]}) => true
 * columnIsNumeric({..., rows: [[null],[null]]}) => false
 * columnIsNumeric({..., rows: [[null],['hello']]}) => false
 */
export function columnIsNumeric({rows}: RunQueryOutputLegacyData, columnIndex: number): boolean {
  for (const row of rows) {
    if (row[columnIndex] == null) {
      continue;
    }
    return typeof row[columnIndex] === 'number';
  }
  return false;
}

/**
 * Return sorted version of input RunQueryOutput based on the input SortState. Will be sorted by
 * resolved chip name if rawOutputMode = false and the cells are resolvable to chip names using
 * chipItemIdMap.
 */
export default function sortedRunQueryOutput(
  runQueryOutput: RunQueryOutputLegacyData,
  sortState: RunQueryOutputTableSortState,
  chipItemIdMap: ResolvedChipItemIdMap,
  rawOutputMode: boolean
): RunQueryOutputLegacyData {
  if (sortState == null) {
    return runQueryOutput;
  }

  let rows1: Array<Row>;
  if (columnIsNumeric(runQueryOutput, sortState.index)) {
    rows1 = _.sortBy(runQueryOutput.rows, (row) => row[sortState.index]);
  } else {
    rows1 = naturalSortByFunction(runQueryOutput.rows, (row) =>
      cellToString(rawOutputMode ? row[sortState.index] : resolveCell(row[sortState.index], chipItemIdMap))
    );
  }

  // nulls sorted to bottom (or top for ascending mode)
  const rows2 = _.sortBy(rows1, (row) => (row[sortState.index] == null ? -1 : 0));

  if (sortState.descending) {
    rows2.reverse();
  }

  return {...runQueryOutput, rows: rows2};
}

/**
 * Sorts the input RunQueryOutput according to input sortState assuming all cells have already been resolved.
 */
export function sortedResolvedRunQueryOutput(
  runQueryOutput: RunQueryOutputLegacyData,
  sortState: RunQueryOutputTableSortState
): RunQueryOutputLegacyData {
  return sortedRunQueryOutput(runQueryOutput, sortState, {}, true);
}
